import type { CalendarOccurrence } from '../../calendar-domain.js';
import type { CalendarEvent } from '../calendar-view-types.js';
import type { CalendarEventInteractionContext } from './calendar-event-interactions.js';

type CalendarClipboardEntry = {
  event: CalendarEvent;
  mode: 'copy' | 'cut';
  removeSource?: () => void;
};

let calendarClipboard: CalendarClipboardEntry | undefined;

function cloneEvent(event: CalendarEvent): CalendarEvent {
  return {
    ...event,
    endAt: new Date(event.endAt),
    startAt: new Date(event.startAt),
  };
}

export function calendarClipboardHasEvent(): boolean {
  return calendarClipboard !== undefined;
}

export function copyCalendarOccurrence(
  occurrence: CalendarOccurrence<CalendarEvent>
): void {
  calendarClipboard = {
    event: cloneEvent(occurrence.source),
    mode: 'copy',
  };
}

export function cutCalendarOccurrence(
  occurrence: CalendarOccurrence<CalendarEvent>,
  removeSource: () => void
): void {
  calendarClipboard = {
    event: cloneEvent(occurrence.source),
    mode: 'cut',
    removeSource,
  };
}

export function pasteCalendarOccurrence(
  context: CalendarEventInteractionContext
): string | undefined {
  const entry = calendarClipboard;
  if (!entry || context.readonly()) return;

  const view = context.view;
  view.ensureCalendarColumns();
  const rowId = context.addRow();
  if (!rowId) return;
  const event = entry.event;

  context.setCellJson(
    rowId,
    view.mainProperties$.value.titleColumn,
    event.title
  );
  context.setCellJson(rowId, view.startDateCol$.value, event.startAt.getTime());
  context.setCellJson(rowId, view.endDateCol$.value, event.endAt.getTime());
  context.setCellJson(rowId, view.allDayCol$.value, event.allDay);
  context.setCellJson(rowId, view.timezoneCol$.value, event.timezone);
  // Placement is the containing calendar database. A provider calendar
  // identity must never leak from the source into a different destination.
  context.setCellJson(rowId, view.calendarIdCol$.value, '');
  context.setCellJson(
    rowId,
    view.locationPhysicalCol$.value,
    event.locationPhysical
  );
  context.setCellJson(
    rowId,
    view.locationVirtualCol$.value,
    event.locationVirtual
  );
  context.setCellJson(rowId, view.attendeesCol$.value, event.attendees);
  context.setCellJson(rowId, view.reminderCol$.value, event.reminder);
  context.setCellJson(rowId, view.descriptionCol$.value, event.description);
  context.setCellJson(rowId, view.recurrenceCol$.value, event.recurrence);
  context.setCellJson(
    rowId,
    view.recurrenceExdateCol$.value,
    event.recurrenceExdate
  );
  context.setCellJson(rowId, view.recurrenceIdCol$.value, event.recurrenceId);
  context.setCellJson(rowId, view.statusCol$.value, event.status);
  context.setCellJson(rowId, view.visibilityCol$.value, event.visibility);
  context.setSelectedOccurrence(rowId, event.startAt.toISOString());

  if (entry.mode === 'cut') {
    calendarClipboard = undefined;
    entry.removeSource?.();
  }
  return rowId;
}

export function clearCalendarClipboardForTests(): void {
  calendarClipboard = undefined;
}
